import { Suspense, useEffect } from 'react';
import { Button, ConfigProvider, Result, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import zhTW from 'antd/locale/zh_TW';
import enUS from 'antd/locale/en_US';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppLayout } from './layouts/AppLayout';
import { firstPermittedRoute, RoutePermissionBoundary } from './components/RoutePermissionBoundary';
import { appRoutes } from './routes';
import { authStore, useAdminAuthState } from './stores/auth';
import { I18nProvider, useI18n } from './i18n';
import { LocaleSwitcher } from './components/LocaleSwitcher';
import './styles/theme.css';
import './styles/governance.css';

const fullPage = { minHeight: '100vh', display: 'grid', placeItems: 'center' } as const;

function ProtectedRoutes() {
  const fallbackPath = firstPermittedRoute(appRoutes);
  return <Routes>
    {appRoutes.filter((route) => !route.public).map((route) => <Route
      key={route.key}
      path={route.path}
      element={<RoutePermissionBoundary route={route} fallbackPath={fallbackPath}>{route.element}</RoutePermissionBoundary>}
    />)}
    <Route path="*" element={<Navigate to={fallbackPath} replace />} />
  </Routes>;
}

function ProtectedAdminApp() {
  const auth = useAdminAuthState();
  const { t } = useI18n();
  const location = useLocation();

  useEffect(() => {
    if (auth.authenticated && auth.accessStatus === 'idle') {
      void authStore.hydrateAccess().catch(() => undefined);
    }
  }, [auth.authenticated, auth.accessStatus]);

  if (!auth.authenticated) {
    const returnUrl = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }
  if (auth.accessStatus === 'idle' || auth.accessStatus === 'loading') {
    return <div style={fullPage}><Spin size="large" tip={t('auth.permission_loading')} /></div>;
  }
  if (auth.accessStatus === 'error') {
    return <div style={fullPage}>
      <Result
        status="warning"
        title={t('errors.permission_load_failed')}
        subTitle={t('errors.permission_unavailable')}
        extra={[
          <Button key="retry" type="primary" onClick={() => void authStore.hydrateAccess(true).catch(() => undefined)}>{t('auth.check_again')}</Button>,
          <Button key="logout" onClick={() => authStore.signOut()}>{t('auth.sign_out')}</Button>,
        ]}
      />
    </div>;
  }

  return <AppLayout><ProtectedRoutes /></AppLayout>;
}

function LoginPageRoute() {
  const route = appRoutes.find((item) => item.public);
  return <><div className="login-locale-control"><LocaleSwitcher /></div>{route?.element ?? <Navigate to="/dashboard" replace />}</>;
}

function LocalizedAdminApp() {
  const { locale, t } = useI18n();
  const antdLocale = locale === 'zh-TW' ? zhTW : locale === 'en-US' ? enUS : zhCN;
  return <ConfigProvider locale={antdLocale} theme={{ token: { colorPrimary: '#1677ff', borderRadius: 10, colorBgLayout: '#f5f7fb' } }}>
    <BrowserRouter>
      <Suspense fallback={<div style={fullPage}><Spin size="large" tip={t('common.page_loading')} /></div>}>
        <Routes>
          <Route path="/login" element={authStore.isAuthenticated() ? <Navigate to="/dashboard" replace /> : <LoginPageRoute />} />
          <Route path="/*" element={<ProtectedAdminApp />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </ConfigProvider>;
}

export function App() {
  return <I18nProvider><LocalizedAdminApp /></I18nProvider>;
}

export default App;
